import { useEffect, useState } from 'react';
import { api, type Category } from '../api';

const CATEGORY_ICONS: Record<string, string> = {
  'ulun': '🍃',
  'ulun-aromatizirovannyy': '🌿',
  'aromatizirovannye-uluny': '🌿',
  'zelenyy': '🍵',
  'zelenyy-chay': '🍵',
  'chernyy': '🫖',
  'chernyy-chay': '🫖',
  'fitochay': '🌾',
  'dobavki-i-travy': '🌾',
  'roypush': '🌺',
  'aromatizirovannyy-roypush': '🌺',
  'fruktovyy': '🍓',
  'fruktovyy-aromatizirovannyy-chay': '🍓',
};

function getCategoryIcon(slug: string): string {
  const lower = slug.toLowerCase();
  for (const [key, icon] of Object.entries(CATEGORY_ICONS)) {
    if (lower.includes(key)) return icon;
  }
  return '🍵';
}

export function Assortment() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .categories()
      .then((cats) => {
        const exclude = /ozon|сенча|анчан|sencha|anchan/i;
        const filtered = cats.filter(
          (c) => !exclude.test(c.slug) && !exclude.test(c.name)
        );
        setCategories(filtered.slice(0, 8)); // 6–8 превью категорий
      })
      .catch(() => {
        setCategories([]);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <section id="assortment" className="py-16 sm:py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-tea-900">Категории чая</h2>
            <p className="mt-3 text-lg text-tea-600 max-w-xl mx-auto">
              Категории премиального чая и травяных сборов — полный каталог в Telegram
            </p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="p-6 rounded-xl bg-tea-50 border border-tea-100 animate-pulse">
                <div className="w-12 h-12 rounded-lg bg-tea-200 mb-4" />
                <div className="h-4 bg-tea-200 rounded w-3/4 mb-2" />
                <div className="h-3 bg-tea-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (categories.length === 0) {
    return null;
  }

  return (
    <section id="assortment" className="py-16 sm:py-24 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-tea-900">Категории чая</h2>
          <p className="mt-3 text-lg text-tea-600 max-w-xl mx-auto">
            {categories.length > 0 
              ? `${categories.length} категорий премиального чая и травяных сборов. Все категории с актуальными ценами — в Telegram-боте.`
              : 'Премиальный чай и травяные сборы. Все категории с актуальными ценами — в Telegram-боте.'}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {categories.map((category) => {
            const imageUrl = category.slug ? `https://sntea.online/images/products/${category.slug}.jpg` : null;
            return (
              <div
                key={category.id}
                className="group p-6 rounded-xl bg-white border border-tea-100 hover:border-tea-300 hover:shadow-md transition-all"
              >
                <div className="relative w-full aspect-square rounded-lg bg-tea-50 overflow-hidden mb-4">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent && !parent.querySelector('.category-fallback')) {
                          const fallback = document.createElement('div');
                          fallback.className = 'category-fallback w-full h-full flex items-center justify-center text-4xl';
                          fallback.textContent = getCategoryIcon(category.slug);
                          parent.appendChild(fallback);
                        }
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl">
                      {getCategoryIcon(category.slug)}
                    </div>
                  )}
                </div>
                <h3 className="font-bold text-tea-900 text-lg">{category.name}</h3>
                {category.description && (
                  <p className="mt-2 text-sm text-tea-600 line-clamp-2">{category.description}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
